import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAbpPermissions } from '../../../contexts/AbpPermissionsContext';
import { LicenseStatus, LicenseType } from '../../../types/manageLicenses';
import type { ManageLicenseListDto, BulkLicenseActionResultDto } from '../../../types/manageLicenses';
import LicenseRevokeDialog from './dialogs/LicenseRevokeDialog';
import LicenseSuspendDialog from './dialogs/LicenseSuspendDialog';
import LicenseReactivateDialog from './dialogs/LicenseReactivateDialog';
import LicenseExtendDialog from './dialogs/LicenseExtendDialog';

interface Props {
  license: ManageLicenseListDto;
  onClose: () => void;
}

type DialogKey = 'revoke' | 'suspend' | 'reactivate' | 'extend' | null;

function StatusChip({ status }: { status: LicenseStatus }) {
  const { t } = useTranslation();
  switch (status) {
    case LicenseStatus.Active:
      return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800 font-medium">{t('App.Common.LicenseStatus.Active')}</span>;
    case LicenseStatus.Expired:
      return <span className="px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-800 font-medium">{t('App.Common.LicenseStatus.Expired')}</span>;
    case LicenseStatus.Revoked:
      return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 font-medium">{t('App.Common.LicenseStatus.Revoked')}</span>;
    case LicenseStatus.Suspended:
      return <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 font-medium">{t('App.Common.LicenseStatus.Suspended')}</span>;
    default:
      return <span className="px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-700 font-medium">{t('App.Admin.Licenses.LicenseDrawerDetailsTab.StatusUnknown')}</span>;
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1.5 border-b border-neutral-100 last:border-b-0">
      <span className="text-xs text-neutral-500 w-40 shrink-0">{label}</span>
      <span className="text-xs text-neutral-900 flex-1 break-all">{children}</span>
    </div>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

export default function LicenseDrawerDetailsTab({ license, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { hasPermission } = useAbpPermissions();
  const [dialog, setDialog] = useState<DialogKey>(null);

  const canManage = hasPermission('learnleaf.App.ManageLicenses');
  const canRevoke = hasPermission('learnleaf.App.RevokeLicense');
  const canSuspend = hasPermission('learnleaf.App.SuspendLicense');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleCompleted(_result?: BulkLicenseActionResultDto | ManageLicenseListDto) {
    qc.invalidateQueries({ queryKey: ['manage-licenses'] });
    setDialog(null);
    onClose();
  }

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {canManage && (
          <button
            type="button"
            onClick={() => setDialog('extend')}
            className="px-3 py-1.5 text-xs border border-neutral-300 text-neutral-700 rounded hover:bg-neutral-50"
          >
            {t('App.Admin.Licenses.LicenseDrawerDetailsTab.ExtendButton')}
          </button>
        )}
        {canRevoke && (
          <button
            type="button"
            disabled={license.status === LicenseStatus.Revoked}
            onClick={() => setDialog('revoke')}
            className="px-3 py-1.5 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('App.Admin.Licenses.LicenseDrawerDetailsTab.RevokeButton')}
          </button>
        )}
        {canSuspend && (
          <button
            type="button"
            disabled={license.status === LicenseStatus.Suspended}
            onClick={() => setDialog('suspend')}
            className="px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('App.Admin.Licenses.LicenseDrawerDetailsTab.SuspendButton')}
          </button>
        )}
        {canManage && (
          <button
            type="button"
            disabled={license.status !== LicenseStatus.Suspended}
            onClick={() => setDialog('reactivate')}
            className="px-3 py-1.5 text-xs border border-green-300 text-green-700 rounded hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('App.Admin.Licenses.LicenseDrawerDetailsTab.ReactivateButton')}
          </button>
        )}
      </div>

      {/* Fields */}
      <div className="rounded border border-neutral-200">
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldStudentEmail')}>{license.studentEmail}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldStudentName')}>{license.studentName || t('App.Common.EmptyDash')}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldBookTitle')}>{license.bookTitle}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldPublisher')}>{license.publisherName ?? t('App.Common.EmptyDash')}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldBookseller')}>{license.booksellerName ?? t('App.Common.EmptyDash')}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldLicenseType')}>{t(`App.Common.LicenseType.${LicenseType[license.licenseType]}`)}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldStatus')}><StatusChip status={license.status} /></Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldExternalLicenseId')}>{license.externalLicenseId ?? t('App.Common.EmptyDash')}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldMaxDevices')}>{license.maxConcurrentDevices}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldActiveDevices')}>{license.activeDeviceCount}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldActivationDate')}>{fmtDate(license.activationDate)}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldExpirationDate')}>
          {license.expirationDate ? fmtDate(license.expirationDate) : t('App.Admin.Licenses.LicenseDrawerDetailsTab.Perpetual')}
        </Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldCreated')}>{fmtDate(license.creationTime)}</Row>
        <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldLastModified')}>{fmtDate(license.lastModificationTime)}</Row>
        {license.revokedReason && (
          <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldRevokedReason')}>{license.revokedReason}</Row>
        )}
        {license.revokedDate && (
          <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldRevokedDate')}>{fmtDate(license.revokedDate)}</Row>
        )}
        {license.isDeleted && (
          <Row label={t('App.Admin.Licenses.LicenseDrawerDetailsTab.FieldDeleted')}>
            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 font-medium">{t('App.Common.Yes')}</span>
          </Row>
        )}
      </div>

      {/* Dialogs */}
      <LicenseExtendDialog
        open={dialog === 'extend'}
        licenses={[license]}
        onClose={() => setDialog(null)}
        onCompleted={handleCompleted}
      />
      <LicenseRevokeDialog
        open={dialog === 'revoke'}
        licenses={[license]}
        onClose={() => setDialog(null)}
        onCompleted={handleCompleted}
      />
      <LicenseSuspendDialog
        open={dialog === 'suspend'}
        licenses={[license]}
        onClose={() => setDialog(null)}
        onCompleted={handleCompleted}
      />
      <LicenseReactivateDialog
        open={dialog === 'reactivate'}
        licenses={[license]}
        onClose={() => setDialog(null)}
        onCompleted={handleCompleted}
      />
    </div>
  );
}
